import React, { useCallback, useEffect, useState } from 'react';
import ReportsMenuBar, { ReportMenuBarSelection } from 'src/pages/client/reports/components/reports-menu-bar';
import MetricUniversalDisplayCard from 'src/pages/reports/components/metric-universal-display/metric-universal-display-card';
import { useGetAppDataMutation, useGetBreakdownDataMutation, useGetDomainDataMutation, useGetGeoDataMutation, useGetHourlyDataMutation, useGetReportMetricsMutation, useGetTimeSeriesDataMutation } from 'src/redux/admin/reporting/reporting.endpoints';
import { TimeSeriesDataPoint } from 'src/redux/admin/reporting/reporting.types';
import handleReportDownload from 'src/pages/reports/utils/download-report.utils';
import { ReduxState } from 'src/redux/redux-state';
import { useSelector } from 'react-redux';
import { ClientView } from 'src/redux/organization/organization.types';
import { FALLBACK_DEFAULT_CLIENT } from 'src/redux/organization/organization.types';
import { calculateRate, calculateRateDifference } from 'src/pages/reports/utils/calculated-values.utils';
import { HourlyMetrics, TimeOfDayDisplay } from 'src/pages/reports/components/metric-time-of-day-display';
import { ZipcodeHeatmapDisplay, ZipcodeMetrics } from 'src/pages/reports/components/metric-zipcode-display';
import MetricSourceRank, { MetricRankDisplayData } from 'src/pages/reports/components/metric-source-display/metric-rank-display';
import MetricPercentDisplay from 'src/pages/reports/components/metric-percent-chart/metric-percent-chart';
import MetricBreakdownTable from 'src/pages/reports/components/metric-breakdown-table/metric-breakdown-table';
import transformHourlyData from 'src/pages/reports/utils/hourly-data.utils';
import { DEFAULT_VIEW_SETTINGS, ViewSettings } from 'src/pages/client-management/types/client-view-settings.types';
import ClientViewCustomizeWrapper from './client-view-customize-wrapper';
import { Pencil } from 'lucide-react';


const ClientViewCustomize: React.FC = () => {
  const [viewSettings, setViewSettings] = useState<ViewSettings>(DEFAULT_VIEW_SETTINGS);

  const userState = useSelector((state: ReduxState) => state.userState);
  const urlParams = new URLSearchParams(location.search);
  const previewClientId = urlParams.get('previewClientId');
  const isClientView = userState.permissions?.groups?.includes('client_user') || userState.permissions?.groups?.includes('client_admin');

  const [selection, setSelection] = useState<ReportMenuBarSelection>({} as ReportMenuBarSelection);
  const [defaultView, setDefaultView] = useState<boolean>(true);
  const [formattedTimeSeriesData, setFormattedTimeSeriesData] = useState<TimeSeriesDataPoint[]>([]);
  const [videoCompletionRateData, setVideoCompletionRateData] = useState<{ percentage: number, percentChange: number }>({ percentage: 0, percentChange: 0 });
  const [audioCompletionRateData, setAudioCompletionRateData] = useState<{ percentage: number, percentChange: number }>({ percentage: 0, percentChange: 0 });

  const [fetchReportMetrics, { data: metricsData, isLoading: isLoadingMetrics, error: metricsError }] = useGetReportMetricsMutation();
  const [fetchTimeSeriesData, { data: rawTimeSeriesData, isLoading: isLoadingTimeSeries, error: timeSeriesError }] = useGetTimeSeriesDataMutation();
  const [fetchGeoData, { data: geoData, isLoading: isLoadingGeoData, error: geoError }] = useGetGeoDataMutation();
  const [fetchHourlyData, { data: rawHourlyData, isLoading: isLoadingHourlyData, error: hourlyError }] = useGetHourlyDataMutation();
  const [fetchDomainData, { data: rawDomainData, isLoading: domainLoading, error: domainError }] = useGetDomainDataMutation();
  const [fetchAppData, { data: rawAppData, isLoading: appLoading, error: appError }] = useGetAppDataMutation();
  const [fetchBreakdownData, { data: rawPerformanceData, isLoading: isLoadingPerformanceData, error: performanceError }] = useGetBreakdownDataMutation();

  const handleInitialLoad = () => {
    // set defaults on the view if they do not exist TODO this should happen when the client is created
  };

  const handleUpdateUniversalMetricCard = () => {

  };

  const getCurrentClientView = useCallback(() => {
    if (!selection.accounts.length) return FALLBACK_DEFAULT_CLIENT;

    // Helper function to find the appropriate view
    const findView = (views: ClientView[] | undefined) => {
      if (!views) return null;

      // If multiple accounts are selected, use client's default view
      if (selection.accounts.length > 1) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }

      // Find view for current account
      const currentView = views.find((view: ClientView) => view.account_ext_id === selection.accounts[0].id.toString());
      const notConfigured = currentView &&
        (!currentView.metrics?.length) &&
        (!currentView.graph_one?.metric_one && !currentView.graph_one?.metric_two) &&
        (!currentView.graph_two?.metric_one && !currentView.graph_two?.metric_two) &&
        (!currentView.graph_three?.metric_one && !currentView.graph_three?.metric_two);

      if (notConfigured) {
        return views.find((view: ClientView) => view.account_ext_id === 'default');
      }
      return currentView;
    };

    // If in preview mode, use the preview client's view configuration
    if (previewClientId && userState.client) {
      return findView(userState.client.views) as ClientView ?? FALLBACK_DEFAULT_CLIENT;
    }

    // Regular client view logic
    if (!isClientView) return FALLBACK_DEFAULT_CLIENT;
    return findView(userState.client?.views) as ClientView ?? FALLBACK_DEFAULT_CLIENT;
  }, [isClientView, selection.accounts, userState.client?.views, previewClientId, userState.client]);

  const shouldShowMetric = useCallback((metric: string) => {
    if (isClientView || previewClientId) {
      const currentView = getCurrentClientView();
      const included = currentView?.metrics.includes(metric) ?? false;
      if (!included) return false;
    }

    if (metric === 'geo') {
      return geoData?.length || 0 > 0 || isLoadingGeoData;
    }
    if (metric === 'domain') {
      return rawDomainData?.length || 0 > 0 || domainLoading;
    }
    if (metric === 'app') {
      return rawAppData?.length || 0 > 0 || appLoading;
    }
    if (metric === 'hourly') {
      return rawHourlyData?.length || 0 > 0 || isLoadingHourlyData;
    }

    if (!metricsData?.current) return true;
    if (metric === 'video_completion_rate') {
      return !!(metricsData?.current && metricsData.current.video_starts > 0);
    }
    if (metric === 'audio_completion_rate') {
      return !!(metricsData?.current && metricsData.current.audio_starts > 0);
    }
    const value = metricsData.current[metric];
    return typeof value !== 'undefined' && value !== 0;
  }, [isClientView, previewClientId, getCurrentClientView, metricsData, geoData, rawDomainData, rawAppData, rawHourlyData, isLoadingGeoData, domainLoading, appLoading, isLoadingHourlyData]);

  useEffect(() => {
    if (selection.source && selection.accounts.length > 0 && selection.startDate && selection.endDate) {
      fetchReportMetrics({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
      fetchTimeSeriesData({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
      fetchGeoData({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
      fetchHourlyData({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
      fetchDomainData({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
      fetchAppData({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
      fetchBreakdownData({
        source: selection.source,
        account_ids: selection.accounts.map(account => account.id),
        campaign_ids: selection.campaigns.map(campaign => campaign.id),
        line_item_ids: selection.lineItems.map(lineItem => lineItem.id),
        start_date: selection.startDate,
        end_date: selection.endDate,
      });
    }
  }, [selection]);

  useEffect(() => {
    setVideoCompletionRateData({
      percentage: calculateRate(metricsData?.current?.video_starts ?? 0, metricsData?.current?.video_completes ?? 0),
      percentChange: calculateRateDifference(metricsData?.current?.video_starts ?? 0, metricsData?.current?.video_completes ?? 0, metricsData?.dif?.video_starts ?? 0, metricsData?.dif?.video_completes ?? 0),
    });
    setAudioCompletionRateData({
      percentage: calculateRate(metricsData?.current?.audio_starts ?? 0, metricsData?.current?.audio_completes ?? 0),
      percentChange: calculateRateDifference(metricsData?.current?.audio_starts ?? 0, metricsData?.current?.audio_completes ?? 0, metricsData?.dif?.audio_starts ?? 0, metricsData?.dif?.audio_completes ?? 0),
    });
  }, [metricsData]);

  useEffect(() => {
    if (rawTimeSeriesData) {
      setFormattedTimeSeriesData(rawTimeSeriesData.map((point: any) => ({
        ...point,
        video_completion_rate: point.video_starts > 0 ? (point.video_completes / point.video_starts) * 100 : 0,
        audio_completion_rate: point.audio_starts > 0 ? (point.audio_completes / point.audio_starts) * 100 : 0,
      })));
    }

  }, [rawTimeSeriesData]);


  function handleHourClick(hour: number, metrics: HourlyMetrics): void {
    throw new Error('Function not implemented.');
  }

  return (
    <div className="h-full flex flex-col space-y-4 p-6 pt-0 gap-4 pb-10">
      <div className="flex-1 space-y-8 pb-[100px]">
        <div className='relative'>
          {defaultView && (
            <div className='absolute left-0 -top-7 items-center flex flex-row'>
              <Pencil className='size-3 mr-1 text-blue-500' />
              <span className='text-xs text-blue-500'>
                Editing Default View
              </span>
            </div>
          )}
          <ReportsMenuBar
            className='bg-gray-201 text-black rounded-full'
            onSelectionChange={(selection) => setSelection(selection) }
            onDateChange={() => { } }
            onEditToggle={function(): void {
              throw new Error('Function not implemented.');
            } }
          />
          <div className='absolute right-0 -top-7 items-center flex flex-row'>
            <span className='text-xs text-gray-500 mr-1'>
              Default
            </span>
            <input
              checked={defaultView}
              onChange={() => setDefaultView(!defaultView)}
              type='checkbox'
              className='rounded-full bg-blue-500 text-white px-2 py-0.5 text-xs border-0'
            />
          </div>
        </div>
        <div
          style={{ marginTop: '14px' }}
        >
          <ClientViewCustomizeWrapper>
            <MetricUniversalDisplayCard
              metricsData={metricsData}
              isLoading={isLoadingMetrics || isLoadingTimeSeries}
              aggregateData={metricsData?.current || {impressions: 0, spend: 0, conversions: 0, clicks: 0}}
              timeSeriesData={formattedTimeSeriesData}
              startDate={selection.startDate}
              endDate={selection.endDate}
              handlePlatformClick={() => { } }
              handleDownload={() => handleReportDownload(selection.source, selection.accounts.map(account => account.id), selection.campaigns.map(campaign => campaign.id), selection.startDate, selection.endDate)}
            />
          </ClientViewCustomizeWrapper>
        </div>


        <div className="flex justify-between w-full gap-8">
          <ClientViewCustomizeWrapper
            className='w-3/4'
          >
            <ZipcodeHeatmapDisplay
              className='w-full'
              data={geoData && geoData.length > 0 ? geoData as unknown as ZipcodeMetrics[] : []}
              config={{
                selectedMetric: 'impressions',
                showLegend: true,
                showTooltips: true,
                mapHeight: 400,
              }}
              onZipcodeClick={(zipcode) => console.log('Selected:', zipcode)}
            />
          </ClientViewCustomizeWrapper>

          <ClientViewCustomizeWrapper
            className='w-1/4'
            bottom
          >
            <MetricSourceRank
              title="Zipcode"
              labelKey="Zipcode"
              data={geoData && geoData.length > 0 ? geoData as unknown as MetricRankDisplayData[] : []}
              className="w-full"
            />
          </ClientViewCustomizeWrapper>
        </div>
        <div>
          <ClientViewCustomizeWrapper>
            <TimeOfDayDisplay
              data={rawHourlyData ? transformHourlyData(rawHourlyData) : []}
              config={{
                selectedMetric: 'impressions',
                chartType: 'bar',
                showPeakHours: false,
                show24Hour: true,
                showGrid: true,
                showTooltips: true,
                chartHeight: 500,
                colorScheme: 'blue',
                highlightPeakHours: false,
                showAverageLines: true,
              }}
              onHourClick={handleHourClick}
              title="Hourly Performance"
              className="w-full"
            />
          </ClientViewCustomizeWrapper>
        </div>
        <div className="flex w-full gap-8">
          {/* omit if dv360 and data exists */}
          <ClientViewCustomizeWrapper
            className='w-1/3'
            bottom
          >
            <MetricSourceRank
              title="Domain"
              labelKey="Domain"
              data={rawDomainData ? rawDomainData as unknown as MetricRankDisplayData[] : []}
              initialMetric="impressions"
              className="w-full"
            />
          </ClientViewCustomizeWrapper>

          <ClientViewCustomizeWrapper
            className='w-1/3'
            bottom
          >
            <MetricSourceRank
              title="App"
              labelKey="App"
              data={rawAppData ? rawAppData as unknown as MetricRankDisplayData[] : []}
              initialMetric="impressions"
              className="w-full"
            />
          </ClientViewCustomizeWrapper>


        </div>
        <div className="flex w-full gap-8">
          <ClientViewCustomizeWrapper
            className=''
            fieldSelection={false}
          >
            <MetricPercentDisplay
              title="Video Completion Rate"
              percentage={videoCompletionRateData.percentage}
              percentChange={videoCompletionRateData.percentChange}
            />
          </ClientViewCustomizeWrapper>
          <ClientViewCustomizeWrapper
            className=''
            fieldSelection={false}
          >
            <MetricPercentDisplay
              title="Platform"
              percentage={audioCompletionRateData.percentage}
              percentChange={audioCompletionRateData.percentChange}
            />
          </ClientViewCustomizeWrapper>
        </div>
        <div className="flex w-full gap-8">
          <ClientViewCustomizeWrapper
            className='w-full'
            fieldSelection={false}
          >
            <MetricBreakdownTable
              data={rawPerformanceData || []}
              isLoading={isLoadingPerformanceData}
              shouldShowMetric={shouldShowMetric as (metric: string) => boolean }
              handleDownload={() => handleReportDownload(selection.source, selection.accounts.map(account => account.id), selection.campaigns.map(campaign => campaign.id), selection.startDate, selection.endDate)}
            />
          </ClientViewCustomizeWrapper>
        </div>

      </div>
      <div className='mb-10' />
    </div>
  );
};

export default ClientViewCustomize;